import type {
  CalculationInfoResponse,
  StandaloneRequest,
} from '@kk/api/modules/customerPortal';
import { postCalculationStandalone } from '@kk/api/modules/customerPortal';
import { CalculatorForm, Disclaimer } from '@kk/ui/components/Calculator';
import { useEffect, useState } from 'react';
import {
  CalculationResults,
  type CalculationWithTitle,
} from '#/components/CalculationResults';

function capitalizeFirstLetter(str: string): string {
  return str.charAt(0).toUpperCase() + str.slice(1);
}

export function CalculatorPage() {
  const [calculations, setCalculations] = useState<CalculationWithTitle[]>([]);
  const [error, setError] = useState<string | null>(null);

  // Warn user before leaving if there are unsaved calculations
  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (calculations.length > 0) {
        // Modern browsers require returnValue to be set
        event.preventDefault();
        event.returnValue = '';
        // Some browsers show this message, others show a generic message
        return '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [calculations.length]);

  const handleSubmit = async (
    data: StandaloneRequest,
  ): Promise<CalculationInfoResponse> => {
    setError(null);
    try {
      // Use build-time environment variable
      const apiUrl = import.meta.env.VITE_API_URL;
      const response = await postCalculationStandalone(data, {
        baseUrl: apiUrl,
      });
      return response;
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Beregningen fejlede';
      setError(errorMessage);
      throw err;
    }
  };

  const handleSuccess = (data: CalculationInfoResponse) => {
    const newCalculation: CalculationWithTitle = {
      ...data,
      title: `${capitalizeFirstLetter('beregning')} ${(calculations.length + 1).toString().padStart(2, '0')}`,
    };

    setCalculations((prev) => [...prev, newCalculation]);
    setError(null);
  };

  return (
    <div className="flex flex-col gap-y-6 print:gap-y-0">
      <div className="">
        <div className="py-8 print:hidden">
          <h1 className="typ-headline-xl mb-2 xt-neutral-110">Låneberegner</h1>
          <p className="typ-body-md text-neutral-100 prose">
            Med få klik kan du lave en her-og-nu-beregning af, hvad det koster
            at låne hos os. Du får en oversigt over ydelser, renteomkostninger
            og udviklingen i restgælden gennem hele lånets løbetid.
          </p>
        </div>

        <div className="grid grid-cols-1 screen:lg:grid-cols-3  pb-8 gap-6">
          <CalculatorForm
            className="col-span-2 print:hidden"
            formClassName="rounded-lg bg-white shadow-sm-dl/16"
            onSubmit={handleSubmit}
            onSuccess={handleSuccess}
          />
          <Disclaimer />
        </div>
      </div>

      {error && (
        <div className="rounded-lg bg-red-10 p-4 text-red-100 md:mx-8">
          <p className="font-medium">Der opstod en fejl</p>
          <p className="text-sm">{error}</p>
        </div>
      )}

      {calculations.length > 0 && (
        <CalculationResults calculations={calculations} />
      )}
    </div>
  );
}
